// src/i18n/translateSchemaDeep.ts
import type { TFunction } from "i18next";

type AnyObj = Record<string, any>;

const KEYS_TO_TRANSLATE = new Set([
  "title",
  "description",
  "label",
  "tooltip",
  "placeholder",
  "addonBefore",
  "addonAfter",
  "help",
  "content",
  "x-content",
]);

// Lookup by trimmed key but preserve original leading/trailing whitespace in the output
function translatePreservingWhitespace(
  val: string,
  t: TFunction,
  ns: string,
  explicitKey?: string
) {
  const leading = val.match(/^\s*/)?.[0] ?? "";
  const trailing = val.match(/\s*$/)?.[0] ?? "";
  const trimmed = val.trim(); // for lookup
  const key = explicitKey ?? trimmed ?? val;
  const translated = t(key, { ns, defaultValue: trimmed || val });
  return leading + translated + trailing;
}

export function translateSchemaDeep<T extends AnyObj>(
  schema: T,
  t: TFunction,
  ns = "common"
): T {
  try {
    const seen = new WeakMap();

    const walk = (node: any): any => {
      if (!node || typeof node !== "object") {
        return node;
      }
      if (seen.has(node)) {
        return seen.get(node);
      }

      // arrays
      if (Array.isArray(node)) {
        const arr = node.map(walk);
        seen.set(node, arr);
        return arr;
      }

      // objects
      const out: AnyObj = {};
      seen.set(node, out);

      const explicitKey =
        typeof node["x-i18n-key"] === "string" ? node["x-i18n-key"] : undefined;

      for (const [k, v] of Object.entries(node)) {
        // Direct string props (title, description, etc.)
        if (typeof v === "string" && KEYS_TO_TRANSLATE.has(k)) {
          out[k] = translatePreservingWhitespace(
            v,
            t,
            ns,
            explicitKey ? `${explicitKey}.${k}` : undefined
          );
          continue;
        }

        // x-component-props / x-decorator-props: normalize keys & translate known string props
        if (
          (k === "x-component-props" || k === "x-decorator-props") &&
          v &&
          typeof v === "object"
        ) {
          const cpIn = v as AnyObj;
          const cpOut: AnyObj = {};

          for (const [innerKeyRaw, innerVal] of Object.entries(cpIn)) {
            const innerKey = innerKeyRaw.trim(); // normalize " placeholder" -> "placeholder"

            if (
              typeof innerVal === "string" &&
              KEYS_TO_TRANSLATE.has(innerKey)
            ) {
              cpOut[innerKey] = translatePreservingWhitespace(
                innerVal,
                t,
                ns,
                explicitKey ? `${explicitKey}.props.${innerKey}` : undefined
              );
            } else {
              // still walk nested objects/arrays
              cpOut[innerKey] = walk(innerVal);
            }
          }

          out[k] = cpOut; // already normalized; no need to walk again
          continue;
        }

        // enum / dataSource labels
        if ((k === "enum" || k === "dataSource") && Array.isArray(v)) {
          out[k] = v.map((opt: any, idx: number) => {
            if (opt && typeof opt === "object") {
              const copy: AnyObj = { ...opt };
              if (typeof copy.label === "string") {
                copy.label = translatePreservingWhitespace(
                  copy.label,
                  t,
                  ns,
                  explicitKey ? `${explicitKey}.enum.${idx}.label` : undefined
                );
              }
              if (typeof copy.title === "string") {
                copy.title = translatePreservingWhitespace(
                  copy.title,
                  t,
                  ns,
                  explicitKey ? `${explicitKey}.enum.${idx}.title` : undefined
                );
              }
              return copy;
            }
            return opt;
          });
          continue;
        }

        // Tabs/FormTab labels (common pattern under x-component-props.tab)
        if (k === "items" && Array.isArray(v)) {
          out[k] = v.map((tab: any, idx) => {
            const tabCopy = { ...tab };
            const tabProps = tabCopy?.["x-component-props"];
            if (tabProps && typeof tabProps === "object") {
              const raw = tabProps.tab;
              if (typeof raw === "string") {
                tabCopy["x-component-props"] = {
                  ...tabProps,
                  tab: translatePreservingWhitespace(
                    raw,
                    t,
                    ns,
                    explicitKey ? `${explicitKey}.tabs.${idx}` : undefined
                  ),
                };
              }
            }
            return walk(tabCopy);
          });
          continue;
        }

        // generic recurse
        out[k] = walk(v);
      }

      return out;
    };

    return walk(schema);
  } catch (error) {
    console.error("translateSchemaDeep: Fatal error:", error);
    return schema;
  }
}
